import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DynamicPricingIcon } from '../../../assets/icons/sidebar/dynamic-pricing.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  DynamicPricingProperties,
  RequestPricingInterface,
} from '../../../interface/dynamic-pricing.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableApprovedDynamicPricingFiltersApplied,
  fetchApprovedPricing,
  fetchListDynamicPricingFilters,
  updateApprovedDynamicPricingFilters,
  setSortData,
} from '../../../redux/slices/ApprovedPricingSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import dynamicPricingApi from '../../../utils/api/dynamic-pricing';

/**
 * Approved dynamic pricing table
 */
function ApprovedPricing() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation('pricing');
  const dispatch = useAppDispatch();
  const {
    pricing,
    loading,
    page,
    limit,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.approvedPricing);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
  });

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The data
   */
  const fetchPricing = (data: RequestPricingInterface) => {
    if (!loading) {
      dispatch(fetchApprovedPricing(data));
    }
  };

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof DynamicPricingProperties,
      sortAllow: true,
    },
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof DynamicPricingProperties,
      sortAllow: true,
    },
    {
      label: t('electricityPrice'),
      accessorKey: 'electricityCostByUtilityProvider' as keyof DynamicPricingProperties,
      sortAllow: true,
    },
    {
      label: t('baseRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>{t('baseRate')}</span>
        </div>
      ),
      accessorKey: 'base' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(rowData.base)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
      sortAllow: true,
    },
    {
      label: t('peakRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>{t('peakRate')}</span>
        </div>
      ),
      accessorKey: 'peak' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(rowData.peak)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
      sortAllow: true,
    },
    {
      label: t('offPeakRate'),
      customLabel: (
        <div className="dynamic-pricing-custom-label">
          <span>{t('offPeakRate')}</span>
        </div>
      ),
      accessorKey: 'offpeak' as keyof DynamicPricingProperties,
      /**
       * Document view in table
       * @param {any} rowData - The rowData
       */
      customData: (rowData: DynamicPricingProperties) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(rowData.offpeak)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
      sortAllow: true,
    },
  ];

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('electricityPrice'), accessorKey: 'electricityCostByUtilityProvider' },
    { label: t('baseRate'), accessorKey: 'base' },
    { label: t('peakRate'), accessorKey: 'peak' },
    { label: t('offPeakRate'), accessorKey: 'offpeak' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: RequestPricingInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchPricing({ ...value, ...queryParameters, sortField, sortBy });
  };

  /**
   * Close modal function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
    });
  };

  /**
   * Handle the filter clear function
   */
  const handleClearFilter = () => {
    fetchPricing({
      page: DefaultApiParameters.page,
      limit,
      status: 'approved',
    });
  };

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      status: 'approved',
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    dynamicPricingApi
      .fetchDynamicPricingExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: DynamicPricingProperties, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDate(row.createdAt) : 'N/A';

              case 'name':
                return row.name || 'N/A';

              case 'electricityCostByUtilityProvider':
                return row.electricityCostByUtilityProvider || 'N/A';

              case 'base':
                return row.base
                  ? `${ToFixedValue(row.base)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'peak':
                return row.peak
                  ? `${ToFixedValue(row.peak)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'offpeak':
                return row.offpeak
                  ? `${ToFixedValue(row.offpeak)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'approved-dynamic-pricing-list.csv');
          } else {
            exportPDF(headers, rows, 'approved-dynamic-pricing-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchPricing({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchPricing({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || pricing.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchPricing({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableApprovedDynamicPricingFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('dynamicPricingApproval'),
        icon: <DynamicPricingIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults, t]);

  return (
    <div className="dynamic-pricing-table" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />
      <CustomTable
        header={column}
        data={pricing}
        serialNo
        version="v2"
        filterClearFunction={handleClearFilter}
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        height={window.innerHeight >= 700 ? 390 : 397}
        filters={filters}
        filtersToDispatch={updateApprovedDynamicPricingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListDynamicPricingFilters}
        refreshRequired
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel={t('refresh')}
      />
    </div>
  );
}

export default ApprovedPricing;
